import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import useApi from "@/hooks/useApi";
import RouteSkeleton from "@/components/RouteSkeleton";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import { IntegrationTab } from "../shared/IntegrationTab";
import { SectionCard, FormRow, FieldInput, PrimaryButton, SecondaryButton, Divider, ConfirmDialog } from "@/components/ds";
import { Check } from "@/components/ds/icons";
import CallRailSetupWizard from "./CallRailSetupWizard";

// --- Types ---------------------------------------------------------------------

interface CallRailConfig {
  configured: boolean;
  id?: number;
  accountId?: string;
  isEnabled?: boolean;
  webhookUrl?: string;
  webhookSecretMasked?: string;
  createdAt?: string;
}

type Tab = "overview" | "config" | "activity";

const CAPABILITIES = [
  "Every inbound IVR call arrives tagged with the marketing source, campaign, and medium that drove it",
  "Call recordings linked directly to the attribution record when CallRail provides one",
  "One webhook secret per site, regenerate it any time without losing historical data",
  "Report designed to grow with more attribution providers over time, not locked to CallRail alone",
];

// --- Main component --------------------------------------------------------------

export default function CallRail() {
  const api = useApi();
  const navigate = useNavigate();

  const [tab, setTab] = useState<Tab>("overview");
  const [config, setConfig] = useState<CallRailConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [form, setForm] = useState({ accountId: "", isEnabled: true });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "error">("idle");

  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);

  const [showSetupWizard, setShowSetupWizard] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const resp = await api.admin.get("CallRail/config");
      if (resp.ok) {
        const data: CallRailConfig = await resp.json();
        setConfig(data);
        setForm({
          accountId: data.accountId ?? "",
          isEnabled: data.isEnabled ?? true,
        });
      } else if (resp.status === 404) {
        setConfig({ configured: false });
      } else {
        setLoadError(`Failed to load: HTTP ${resp.status}`);
      }
    } catch (err) {
      setLoadError(`Failed to load: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await api.admin.post("CallRail/config", form);
      if (resp.ok) {
        await load();
      } else {
        const body = await resp.text().catch(() => "");
        setSaveError(`Failed to save (HTTP ${resp.status})${body ? `: ${body}` : ""}`);
      }
    } catch (err) {
      setSaveError(`Network error: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const resp = await api.admin.post("CallRail/config/test-connection", {});
      const body = await resp.json().catch(() => null);
      if (resp.ok) {
        setTestResult({ ok: true, message: body?.message ?? "Connection verified." });
      } else {
        setTestResult({ ok: false, message: body?.message ?? `Connection test failed (HTTP ${resp.status}).` });
      }
    } catch (err) {
      setTestResult({ ok: false, message: `Network error: ${err instanceof Error ? err.message : String(err)}` });
    } finally {
      setTesting(false);
    }
  };

  const copyWebhookUrl = async () => {
    if (!config?.webhookUrl) return;
    try {
      await navigator.clipboard.writeText(config.webhookUrl);
      setCopyStatus("copied");
    } catch {
      setCopyStatus("error");
    } finally {
      setTimeout(() => setCopyStatus("idle"), 2000);
    }
  };

  const regenerateSecret = async () => {
    setRegenerating(true);
    setRegenerateError(null);
    try {
      const resp = await api.admin.post("CallRail/config/regenerate-secret", {});
      if (resp.ok) {
        await load();
        setShowRegenerateConfirm(false);
      } else {
        setRegenerateError(`Failed to regenerate (HTTP ${resp.status}). The old secret is still active.`);
      }
    } catch (err) {
      setRegenerateError(`Network error, the old secret is still active: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) return <RouteSkeleton />;

  const tabsJSX = (
    <>
      <IntegrationTab active={tab === "overview"} onClick={() => setTab("overview")}>Overview</IntegrationTab>
      <IntegrationTab active={tab === "config"} onClick={() => setTab("config")}>{config?.configured ? "Settings" : "Connect"}</IntegrationTab>
      <IntegrationTab active={tab === "activity"} onClick={() => setTab("activity")}>Activity Log</IntegrationTab>
    </>
  );

  return (
    <StickyRouteHeader
      title="CallRail Attribution"
      tabs={tabsJSX}
      headerActions={
        <div className="flex items-center gap-3">
          <SecondaryButton onClick={() => setShowSetupWizard(true)}>Set Up</SecondaryButton>
          <Link
            to="/reports/call-attribution"
            className="text-sm font-medium text-[var(--color-primary)] hover:opacity-80"
          >
            View Call Attribution Report &rarr;
          </Link>
        </div>
      }
    >
      {loadError && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" role="alert">
          {loadError}
        </div>
      )}

      {/* Overview */}
      {tab === "overview" && (
        <SectionCard title="What CallRail Attribution does" description="Marketing call-source attribution attached to every inbound IVR call.">
          <ul className="space-y-2">
            {CAPABILITIES.map((item, i) => (
              <li key={i} className="text-sm text-zinc-600 flex gap-2">
                <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      {/* Config */}
      {tab === "config" && (
        <div className="max-w-2xl space-y-4">
          <SectionCard title="Connection">
            <FormRow label="Enabled" hint="Turn off to stop accepting CallRail webhook events without losing configuration.">
              <input
                type="checkbox"
                aria-label="Enable CallRail integration"
                checked={form.isEnabled}
                onChange={(e) => setForm((f) => ({ ...f, isEnabled: e.target.checked }))}
                className="rounded border-zinc-300"
              />
            </FormRow>
            <Divider />
            <FormRow label="Account ID" hint="Your CallRail account identifier.">
              <input
                type="text"
                aria-label="CallRail Account ID"
                value={form.accountId}
                onChange={(e) => setForm((f) => ({ ...f, accountId: e.target.value }))}
                placeholder="e.g. ACC1234567890"
                className="w-64 border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              />
            </FormRow>

            {saveError && <p className="text-sm text-red-600 mt-2" role="alert">{saveError}</p>}

            <div className="flex items-center gap-2 mt-4">
              <PrimaryButton onClick={save} disabled={saving}>
                {saving ? "Saving..." : config?.configured ? "Update" : "Save"}
              </PrimaryButton>
              <SecondaryButton onClick={testConnection} disabled={testing || !config?.configured}>
                {testing ? "Testing..." : "Test Connection"}
              </SecondaryButton>
            </div>

            <div aria-live="polite" className="mt-2">
              {testResult && (
                <p className={`text-sm ${testResult.ok ? "text-green-700" : "text-red-600"}`}>
                  {testResult.ok ? "Success: " : "Error: "}{testResult.message}
                </p>
              )}
            </div>
          </SectionCard>

          {config?.configured && (
            <SectionCard title="Webhook" description="Paste this URL into your CallRail account's webhook settings so calls flow into Velaro automatically.">
              <FormRow label="Webhook URL">
                <div className="flex items-center gap-2">
                  <FieldInput
                    className="w-80 font-mono text-xs"
                    readOnly
                    aria-label="CallRail webhook URL"
                    value={config.webhookUrl ?? ""}
                  />
                  <SecondaryButton onClick={copyWebhookUrl} aria-label="Copy webhook URL to clipboard">
                    {copyStatus === "copied" ? "Copied" : copyStatus === "error" ? "Copy failed" : "Copy"}
                  </SecondaryButton>
                </div>
              </FormRow>
              <Divider />
              <FormRow label="Webhook Secret" hint="Used to verify that inbound webhook calls really came from CallRail.">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-zinc-500">{config.webhookSecretMasked ?? "Not generated yet"}</span>
                  <SecondaryButton onClick={() => setShowRegenerateConfirm(true)}>
                    Regenerate
                  </SecondaryButton>
                </div>
              </FormRow>
              {regenerateError && <p className="text-sm text-red-600 mt-2" role="alert">{regenerateError}</p>}
            </SectionCard>
          )}
        </div>
      )}

      {/* Activity */}
      {tab === "activity" && <IntegrationActivityLog integrationSlug="CallRail" />}

      <ConfirmDialog
        open={showRegenerateConfirm}
        title="Regenerate webhook secret?"
        message="The current secret will be invalidated immediately. Any webhook events sent by CallRail using the old secret will be rejected until you update your CallRail webhook configuration with the new secret."
        confirmLabel="Regenerate secret"
        variant="danger"
        busy={regenerating}
        onConfirm={regenerateSecret}
        onCancel={() => setShowRegenerateConfirm(false)}
      />

      {showSetupWizard && (
        <CallRailSetupWizard
          webhookUrl={config?.webhookUrl ?? null}
          onClose={() => setShowSetupWizard(false)}
        />
      )}
    </StickyRouteHeader>
  );
}

export function BackToIntegrationLandingCallRail() {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate("/integrations")}
      className="text-sm text-zinc-500 hover:text-zinc-700 flex items-center gap-1"
    >
      &larr; Integrations
    </button>
  );
}
